import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useManufacturer } from '../../contexts/WithManufacturer';
import LegacyReseller from '../../stores/reseller/Reseller';
import { useUser } from '../../hooks/useUser';
import { Reseller as ApiReseller, usePutV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQualificationMutation, useDeleteV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQualificationMutation } from 'randmar-api-client';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, RadioGroup, RadioGroupItem, useToast } from 'randmarcomps';

interface IProps {
  reseller: LegacyReseller | ApiReseller;
  children: React.ReactNode;
}

const qualificationOptions = [
  { value: "", label: "No Qualification Status", id: "qual-none", adminOnly: false },
  { value: "Qualified", label: "Qualified", id: "qual-qualified", adminOnly: false },
  { value: "Do Not Sell", label: "Do Not Sell", id: "qual-dns", adminOnly: false },
  { value: "Suspended", label: "Suspended", id: "qual-suspended", adminOnly: true },
];

const ManageQualificationWrapper: React.FC<IProps> = ({ reseller: rawReseller, children }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = useState(false);
  const [qualificationId, setQualificationId] = useState("");
  const [selectedIndex, setSelectedIndex] = useState("");
  const { isSuperAdmin } = useUser();
  const { toast } = useToast();

  const [insertQualification, { isLoading: isInserting }] = usePutV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQualificationMutation();
  const [removeQualification, { isLoading: isRemoving }] = useDeleteV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQualificationMutation();

  const isLegacy = (r: LegacyReseller | ApiReseller): r is LegacyReseller => 'qualifications' in r;

  const reseller: ApiReseller = isLegacy(rawReseller)
    ? {
      ResellerId: rawReseller.resellerId,
      Name: rawReseller.name,
      Qualifications: rawReseller.qualifications?.map(q => ({
        ManufacturerId: q.manufacturerId,
        QualificationId: q.qualificationId,
      })) || null,
    }
    : rawReseller;

  const handleOpen = () => {
    let qualification = (reseller.Qualifications || []).find(q => q.ManufacturerId === applicationId)?.QualificationId;
    if (!isSuperAdmin && qualification === "Suspended") {
      qualification = "Do Not Sell";
    }

    setQualificationId(qualification || "");

    if (qualification === "Do Not Sell") {
      setSelectedIndex("Do Not Sell");
    } else if (qualification === "Suspended") {
      setSelectedIndex("Suspended");
    } else if (!qualification || qualification === "") {
      setSelectedIndex("");
    } else {
      setSelectedIndex("Qualified");
    }
    setOpen(true);
  };

  const handleConfirm = () => {
    if (selectedIndex === "Suspended") {
      handleInsertQualification("Suspended");
    } else if (selectedIndex === "Do Not Sell") {
      handleInsertQualification("Do Not Sell");
    } else if (selectedIndex === "") {
      handleRemoveQualification();
    } else {
      handleInsertQualification(qualificationId);
    }
  };

  const handleInsertQualification = async (qualId: string) => {
    try {
      await insertQualification({
        routeManufacturerId: applicationId,
        resellerId: reseller.ResellerId || "",
        newValue: qualId || "Qualified"
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `${qualId || "Qualification"} was succesfully added.` });
      setOpen(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: `${qualId || "Qualification"} failed to be inserted.` });
    }
  };

  const handleRemoveQualification = async () => {
    try {
      await removeQualification({
        routeManufacturerId: applicationId,
        resellerId: reseller.ResellerId || ""
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `Qualification succesfully removed.` });
      setOpen(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: `Qualification failed to be removed.` });
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQualificationId(e.target.value);
  };


  const isLoading = isInserting || isRemoving;

  return (
    <>
      <div onClick={handleOpen} className="cursor-pointer">
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Change qualification status for {reseller.Name}</DialogTitle>
          </DialogHeader>
          <RadioGroup
            value={selectedIndex}
            onValueChange={setSelectedIndex}
            className="space-y-1 py-4"
          >
            {qualificationOptions
              .filter(opt => !opt.adminOnly || isSuperAdmin)
              .map(option => (
                <div key={option.id}>
                  <Label htmlFor={option.id} className="flex items-center space-x-3 p-3 rounded-md hover:bg-gray-100 cursor-pointer transition-colors">
                    <RadioGroupItem value={option.value} id={option.id} />
                    <span>{option.label}</span>
                  </Label>
                  {option.value === "Qualified" && selectedIndex === "Qualified" &&
                    <div className="pl-10 pt-1 pb-2">
                      <Label htmlFor="qualificationId" className="text-xs">Qualification Id</Label>
                      <Input
                        autoFocus
                        id="qualificationId"
                        placeholder="Optional, default is 'Qualified'"
                        value={qualificationId}
                        onChange={handleChange}
                        onBlur={() => setSelectedIndex("Qualified")}
                        disabled={isLoading}
                        className="mt-1"
                      />
                    </div>
                  }
                </div>
              ))
            }
          </RadioGroup>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageQualificationWrapper;
